<script setup lang="ts" generic="T = Record<string, any>">
import { VDataTableServer } from 'vuetify/components';
import {
    BBtn, BCol, BIcon, BRow, BTextField, BFilterConditions, BSkeletonLoader,
} from '@/design-system';
import { useTranslation, useScrollbarWidth } from '@/composables';
import {
    computed, nextTick, onMounted, onUnmounted, ref, useSlots, watch,
} from 'vue';
import type { ComponentPublicInstance } from 'vue';
import type { DataTableOptionsType, VuetifyOptionsType } from '@/types/DataTableOptionsType';
import type { FilterConditionType } from '@/types/FilterConditionTypes';
import type { FilterFieldMetadata } from '@/types/FilterMetadataTypes';
import { transformSortBy } from '@/utils/sortUtils';
import { useRoute, useRouter } from 'vue-router';

type PropsType = {
    items?: T[];
    headers?: VDataTableServer['$props']['headers'];
    loading?: boolean;
    itemsLength?: number;
    showSelect?: boolean;
    hideHeader?: boolean;
    canSearch?: boolean;
    canAdd?: boolean;
    density?: VDataTableServer['$props']['density'];
    showExpand?: boolean;
    searchPlaceholder?: string;
    fixedHeader?: boolean;
    canFilter?: boolean;
    filterFields?: FilterFieldMetadata[];
    fetchFilterSuggestions?: (field: string, autocomplete?: string, query?: string) => Promise<string[]>;
    filterSuggestions?: string[];
    filterSuggestionsLoading?: boolean;
    classes?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    items: () => [],
    headers: () => [],
    loading: false,
    itemsLength: 0,
    showSelect: false,
    density: 'comfortable',
    hideHeader: false,
    canAdd: true,
    canSearch: true,
    showExpand: false,
    fixedHeader: true,
    canFilter: false,
    filterFields: () => [],
    filterSuggestions: () => [],
    filterSuggestionsLoading: false,
});

const emit = defineEmits<{
    'update:selected': [selected: T[]];
    'update:options': [options: DataTableOptionsType];
    'update:addNew': [];
    'update:expanded': [expanded: T[]];
}>();

const { t } = useTranslation();
const slots = useSlots();
const route = useRoute();
const router = useRouter();
const itemsPerPage = defineModel<number>('itemsPerPage', { required: false, default: 25 });
const expanded = defineModel<string[]>('expanded', { required: false, default: () => [] });
const search = defineModel<string>('search', { required: false, default: '' });
const tableRef = ref<ComponentPublicInstance | null>(null);
const isTableOverflowX = ref<boolean>(false);
let resizeObserver: ResizeObserver | null = null;

const { updateScrollbarWidth } = useScrollbarWidth('.v-table__wrapper', tableRef);

const filters = ref<FilterConditionType[]>([]);
const currentVuetifyOptions = ref<VuetifyOptionsType | null>(null);

const hasActionsColumn = computed(() => {
    if (!props.headers || props.headers.length === 0) {
        return false;
    }
    const lastHeader = props.headers[props.headers.length - 1];

    return lastHeader?.key === 'actions';
});

const hasTopActions = computed(() => slots['top-actions'] || props.canAdd);

const tableClasses = computed(() => {
    const classes = ['b-data-table'];

    if (isTableOverflowX.value && hasActionsColumn.value) {
        classes.push('b-data-table--sticky-actions');
    }

    if (props.loading && props.items.length === 0) {
        classes.push('b-data-table--hidden-progress-loader');
    }

    if (props.classes) {
        classes.push(props.classes);
    }

    return classes;
});

const calculateTableHeight = (): string | number | undefined => {
    if (!tableRef.value || !props.fixedHeader) {
        return undefined;
    }

    const tableElement: HTMLElement = tableRef.value.$el;
    if (!tableElement) {
        return undefined;
    }

    const wrapper = tableElement.querySelector<HTMLElement>('.v-table__wrapper');
    if (!wrapper) {
        return undefined;
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    const wrapperTop = wrapperRect.top;

    const footer = tableElement.querySelector<HTMLElement>('.v-data-table-footer');
    const footerHeight = footer ? footer.offsetHeight + 24 : 90;

    const bottomPadding = 50;
    const availableHeight = window.innerHeight - wrapperTop - footerHeight - bottomPadding;

    const finalHeight = Math.max(300, availableHeight);

    if (wrapper) {
        wrapper.style.height = `${finalHeight}px`;
        wrapper.style.overflowY = 'auto';
    }

    return `${finalHeight}px`;
};

const tableOverflowX = (): void => {
    if (!tableRef.value) {
        isTableOverflowX.value = false;

        return;
    }

    const tableElement: HTMLElement = tableRef.value.$el;
    if (!tableElement || typeof tableElement.querySelector !== 'function') {
        isTableOverflowX.value = false;

        return;
    }

    let scrollableElement = tableElement.querySelector<HTMLElement>('.v-table__wrapper');

    if (!scrollableElement) {
        const table = tableElement.querySelector<HTMLTableElement>('table');
        scrollableElement = table || tableElement;
    }

    isTableOverflowX.value = scrollableElement.scrollWidth > scrollableElement.clientWidth;
};

const handleOptionsUpdate = (vuetifyOptions: VuetifyOptionsType) => {
    currentVuetifyOptions.value = vuetifyOptions;

    const currentUrlSearch = route.query.search || '';
    const decodedCurrentSearch = typeof currentUrlSearch === 'string' ? decodeURIComponent(currentUrlSearch) : '';
    if (search.value !== decodedCurrentSearch) {
        router.replace({
            query: {
                ...route.query,
                search: (search.value && search.value.trim()) !== '' ? encodeURIComponent(search.value) : undefined,
            },
        });
    }

    const transformedSortBy = transformSortBy(vuetifyOptions.sortBy);
    const transformedOptions: DataTableOptionsType = {
        itemsPerPage: vuetifyOptions.itemsPerPage,
        sortBy: transformedSortBy.sortBy,
        sortDirection: transformedSortBy.sortDirection,
        page: vuetifyOptions.page,
        search: search.value,
        filters: filters.value.length > 0 ? filters.value : undefined,
    };
    emit('update:options', transformedOptions);
};

const init = () => {
    const urlSearch = route.query.search || '';
    if (urlSearch && typeof urlSearch === 'string') {
        const decodedSearch = decodeURIComponent(urlSearch);
        if (search.value !== decodedSearch) {
            search.value = decodedSearch;
        }
    }

    const urlFilters = route.query.filters;
    if (urlFilters && typeof urlFilters === 'string') {
        try {
            const parsedFilters = JSON.parse(decodeURIComponent(urlFilters));
            if (Array.isArray(parsedFilters)) {
                filters.value = parsedFilters;
            }
        } catch (e) {
            console.warn('Failed to parse filters from URL', e);
        }
    }
};

watch(
    filters,
    newFilters => {
        const query = { ...route.query };
        if (newFilters.length > 0) {
            query.filters = encodeURIComponent(JSON.stringify(newFilters));
        } else {
            delete query.filters;
        }
        router.replace({ query });

        if (currentVuetifyOptions.value) {
            handleOptionsUpdate(currentVuetifyOptions.value);
        }
    },
    { deep: true },
);

onMounted(async () => {
    await nextTick(() => {
        if (tableRef.value) {
            const elementToObserve: HTMLElement = tableRef.value.$el;
            if (elementToObserve instanceof Element) {
                const mutationObserver = new MutationObserver(() => {
                    tableOverflowX();
                    updateScrollbarWidth();
                });

                mutationObserver.observe(elementToObserve, {
                    childList: true,
                    subtree: true,
                });

                if (window.ResizeObserver) {
                    resizeObserver = new ResizeObserver(() => {
                        tableOverflowX();
                        updateScrollbarWidth();
                    });
                    resizeObserver.observe(elementToObserve);
                }

                tableOverflowX();
                updateScrollbarWidth();
            }
        }

        window.addEventListener('resize', tableOverflowX);
    });
});

onUnmounted(() => {
    if (resizeObserver) {
        resizeObserver.disconnect();
    }
    window.removeEventListener('resize', tableOverflowX);
});

init();

defineOptions({ inheritAttrs: false });
</script>

<template>
    <VDataTableServer
        ref="tableRef"
        v-model:items-per-page="itemsPerPage"
        v-model:expanded="expanded"
        :items="items"
        :headers="headers"
        :loading="loading"
        :search="search"
        :items-length="itemsLength"
        :show-select="showSelect"
        :show-expand="showExpand"
        :density="density"
        :fixed-header="fixedHeader"
        sort-asc-icon="mdi-chevron-up"
        sort-desc-icon="mdi-chevron-down"
        :height="fixedHeader ? calculateTableHeight() : undefined"
        :class="tableClasses"
        v-bind="$attrs"
        @update:selected="emit('update:selected', $event)"
        @update:options="handleOptionsUpdate"
        @update:expanded="emit('update:expanded', $event)"
    >
        <template
            v-if="!hideHeader"
            #top
        >
            <BRow class="row-filter">
                <BCol
                    cols="12"
                    :sm="hasTopActions ? 10 : 12"
                    :md="hasTopActions ? 10 : 12"
                    :lg="hasTopActions ? 9 : 12"
                    :class="hasTopActions ? 'order-2 order-sm-1' : ''"
                >
                    <BFilterConditions
                        v-if="canFilter"
                        v-model="filters"
                        v-model:search-text="search"
                        :fields="filterFields"
                        :fetch-value-suggestions="fetchFilterSuggestions"
                        :value-suggestions="filterSuggestions"
                        :loading="loading"
                        :suggestions-loading="filterSuggestionsLoading"
                        :search-placeholder="t('filters.filterOrSearch')"
                    />
                    <BTextField
                        v-else-if="canSearch"
                        v-model="search"
                        :placeholder="props.searchPlaceholder || 'common.search'"
                        :hide-details="true"
                        density="compact"
                        class="search-input"
                    >
                        <template #prepend-inner>
                            <BIcon
                                icon="mdi-magnify"
                                size="small"
                                class="opacity-50"
                            />
                        </template>
                    </BTextField>
                </BCol>

                <BCol
                    v-if="hasTopActions"
                    cols="auto"
                    class="text-right order-1 order-sm-2 ml-auto"
                >
                    <slot
                        v-if="$slots['top-actions']"
                        name="top-actions"
                    />
                    <BBtn
                        v-else-if="canAdd"
                        icon
                        density="comfortable"
                        @click="emit('update:addNew')"
                    >
                        <BIcon
                            icon="mdi-plus"
                            size="small"
                        />
                    </BBtn>
                </BCol>
            </BRow>
        </template>

        <template
            v-if="items.length === 0"
            #loading
        >
            <BSkeletonLoader
                class="w-100 table-skeleton-loader"
                color="transparent"
                type="table-row@25"
                :loading="items.length === 0"
            />
        </template>

        <template
            v-for="(_, slotName) in $slots"
            #[slotName]="slotProps"
        >
            <slot
                :name="slotName"
                v-bind="slotProps"
            />
        </template>
    </VDataTableServer>
</template>

<style scoped lang="scss">
$breakpoint-xs: 600px;
.b-data-table {
    container-type: inline-size;
    container-name: b-data-table;
}
.b-data-table :deep(.v-table__wrapper .v-data-table__td) {
    padding-inline: var(--data-table-cell-padding-inline);
}
:deep(.v-data-table-header__content > span) {
    font-weight: 400;
    font-size: 13px;
    white-space: nowrap;
    text-overflow: ellipsis;
    text-transform: uppercase;
    color: rgba(var(--v-theme-data-table-header));
}
:deep(.v-data-table__tr) {
    transition: background-color 100ms;
    color: rgb(var(--v-theme-data-table-content));
    &:hover {
        background-color: rgba(208, 212, 241, 0.1);
    }
}
:deep(.v-data-table-footer) {
    margin-top: 16px;
    flex-flow: column wrap;
    color: rgb(var(--v-theme-data-table-content));

    @container b-data-table (width > #{$breakpoint-xs}) {
        flex-flow: row nowrap;
    }
}
:deep(.v-data-table-footer__items-per-page) {
    flex-flow: column wrap;
    @container b-data-table (width > #{$breakpoint-xs}) {
        flex-flow: row nowrap;
    }
    > span {
        padding-inline-end: 0;
        @container b-data-table (width > #{$breakpoint-xs}) {
            padding-inline-end: 8px;
        }
    }
    > .v-select > .v-input__control {
        margin-inline: auto;
        margin-top: 0;
        @container b-data-table (width > #{$breakpoint-xs}) {
            margin-inline: unset;
        }

        > .v-field {
            border-radius: 12px;
            > .v-field__input {
                --v-input-control-height: 36px;
                --v-field-input-padding-top: 4px;
                --v-field-input-padding-bottom: 4px;
            }
        }
    }
}
:deep(.v-data-table-footer__info) {
    margin-inline: auto;
    min-width: auto;
    margin-block: 12px;
    @container b-data-table (width > #{$breakpoint-xs}) {
        margin-inline: unset;
        margin-block: 0;
        min-width: 116px;
    }
}
:deep(.v-data-table-footer__pagination) {
    margin-inline: auto;
    @container b-data-table (width > #{$breakpoint-xs}) {
        margin-inline: unset;
    }
}
:deep(.v-data-table__td) {
    white-space: nowrap;
}

.b-data-table--sticky-actions {
    :deep(.v-data-table__th:last-child),
    :deep(.v-data-table__td:last-child) {
        position: sticky;
        right: calc(-1 * var(--scrollbar-width, 0px));
        z-index: 1;
        background-color: white;
        box-shadow: -4px 0 6px -2px rgba(0, 0, 0, 0.1);
    }

    :deep(.v-data-table__th:last-child) {
        box-shadow: inset 0 -1px rgba(var(--v-border-color), var(--v-border-opacity));
        background-color: white;
    }
}

.b-data-table {
    :deep(.v-data-table__tr:not(:last-child) > td),
    :deep(.v-data-table__tr:not(:last-child) > th) {
        border-bottom: 1px solid rgba(var(--v-theme-grey-100));
    }

    :deep(.v-data-table-header__content .v-data-table-header__sort-icon) {
        margin-top: 1px;
        margin-left: 4px;
    }
}

:deep(.search-input .v-field--variant-outlined .v-field__outline) {
    display: none;
}
:deep(.search-input .v-field__overlay) {
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 15px;
}
:deep(.search-input .v-field__input) {
    min-height: 36px;
    padding-block: 6px;
}
:deep(.v-table__wrapper .v-data-table-rows-loading) {
    color: rgba(var(--v-theme-data-table-content));
}
.row-filter {
    margin: 0;
    align-items: center;
}
.row-filter > .b-col {
    padding-inline: calc(var(--page-padding-inline) - var(--container-content-padding-inline));
}
.table-page-list :deep(.v-table__wrapper) {
    padding-inline: calc(
        var(--page-padding-inline) - var(--container-content-padding-inline) - var(--data-table-cell-padding-inline)
    );
}
:deep(.v-data-table-progress .v-progress-linear__determinate),
:deep(.v-data-table-progress .v-progress-linear__indeterminate) {
    background: rgb(var(--v-theme-grey-400));
}
.table-skeleton-loader :deep(.v-skeleton-loader__table-row) {
    margin-inline: 0;
}
.table-skeleton-loader :deep(.v-skeleton-loader__text) {
    margin: 6px 0;
    height: 24px;
    background: rgba(var(--v-theme-grey-100));
    border-radius: 0;
}
.table-skeleton-loader :deep(.v-skeleton-loader__table-row > .v-skeleton-loader__text:first-child) {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
}
.table-skeleton-loader :deep(.v-skeleton-loader__table-row > .v-skeleton-loader__text:last-child) {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
}
.b-data-table--hidden-progress-loader :deep(.v-data-table-progress) {
    display: none;
}
</style>
